import { useT } from "@transifex/react";
import { AxiosResponse } from "axios";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import NoOwnContentModal from "../../../../basic/create/noOwnContentModal/NoOwnContentModal";
import InformationBanner from "../../../../basic/informationBanner/InformationBanner";
import { CreationModes } from "../../../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../../../context/AppGeneralContext";
import { useBookContext } from "../../../../context/BookContext";
import { useTestCreationContext } from "../../../../context/TestCreationContext";
import { SourceType } from "../../../../enums/sources";
import { postRequest } from "../../../../helpers/networkHelper";
import { useLogger } from "../../../../hooks/useLogger";
import { AdditionalInfoWrapper } from "./StyledComponents";

/** The banners below the dropdowns: the newsletter sign-up and, before the first test, own content. */
const BookSelectionBanners = () => {
    const {
        openWarningModal,
        userTests,
        decideGoToOwnSubjectsAction,
        openWebAppInAddCardsMode,
        setShouldSubjectSelectionBeDisabled,
    } = useTestCreationContext();
    const { setLastSourceSelected } = useBookContext();
    const { creatingMode, userInfo, setUserInfo } = useGeneralContext();
    const { logEvent } = useLogger();
    const history = useHistory();

    const t = useT();
    const t_useOwnContentInfo = t("Use your own vocabulary in the meantime.", { _tags: "CreateTest,SelectBook" });
    const t_ownContentAnonymous = t("After registering, you can also use your own vocabulary", {
        _tags: "CreateTest,SelectBook",
    });
    const t_notAllBookTitle = t("This function is currently available for almost all books", {
        _tags: "CreateTest,SelectBook",
    });
    const t_notAvailableBook = t(
        "Can't find your book? We will let you know through our teachers Newsletter when we have new content available.",
        { _tags: "CreateTest,SelectBook" }
    );
    const t_notAvailableBookNotRegistered = t(
        "Can't find your book? We will let you know through our teachers Newsletter when we have new content available. The subscription to the Newsletter is only possible after registration or login.",
        { _tags: "CreateTest,SelectBook" }
    );
    const t_signUp = t("Sign up now!", { _tags: "CreateTest,SelectBook" });
    const t_errorNewsletter = t("Error while subscribing to the Newsletter, please try later", {
        _tags: "CreateTest,SelectVocabularySource",
    });
    const t_unconfirmedNewsletter = t(
        "A step is missing! To complete your subscription to the newsletter, please check your email!",
        { _tags: "CreateTest,SelectVocabularySource" }
    );
    const t_continue = t("Continue", { _tags: "CreateTest,SelectVocabularySource" });

    const [isNoOwnContentModalOpen, setIsNoOwnContentModalOpen] = useState(false);
    const [needsSubjectsReload, setNeedsSubjectsReload] = useState(false);

    async function signupToNewsletter() {
        try {
            const subscriptionResponse: AxiosResponse<{ isUnconfirmed: boolean; needsEvent: boolean }> =
                await postRequest("aea/user/newsletter/");

            let subscriptionStatus;

            if (subscriptionResponse.data.hasOwnProperty("error")) {
                openWarningModal(t_errorNewsletter);
                subscriptionStatus = "Error";
            } else {
                if (subscriptionResponse.data.isUnconfirmed) {
                    openWarningModal(t_unconfirmedNewsletter);
                    subscriptionStatus = "unconfirmed";
                } else {
                    openWarningModal("Sie haben unseren Newsletter erfolgreich abonniert.");
                    setUserInfo({ ...userInfo, hasNewsletterSubscription: true });
                    subscriptionStatus = "confirmed";
                }
            }

            logEvent("VTG - Subscribe to newsletter", {
                subscriptionStatus,
            });

            if (subscriptionResponse.data.needsEvent) {
                logEvent("newsletterSubscription");
            }
        } catch (e) {
            openWarningModal(t_errorNewsletter);
        }
    }

    async function handleContinueOwnSubjectsClick() {
        const action = await decideGoToOwnSubjectsAction(needsSubjectsReload);
        if (action === "CONTINUE") {
            setShouldSubjectSelectionBeDisabled(false);
            setLastSourceSelected(SourceType.OWN);
            history.push("/create/select-vocabulary");
        } else if (action === "POPUP") {
            setIsNoOwnContentModalOpen(true);
        }
    }

    function handleGoToWebAppClick() {
        openWebAppInAddCardsMode();
        setIsNoOwnContentModalOpen(false);
        setNeedsSubjectsReload(true);
    }

    return (
        <>
            <AdditionalInfoWrapper className={userInfo.isAnonymousUser ? "bottom-zero" : ""}>
                <InformationBanner
                    iconName={"info"}
                    title={t_notAllBookTitle}
                    isAnonymous={!!userInfo.isAnonymousUser}
                    anonymousText={t_notAvailableBookNotRegistered}
                    registeredText={t_notAvailableBook}
                    showButton={!userInfo.isAnonymousUser && !userInfo.hasNewsletterSubscription}
                    onButtonPress={signupToNewsletter}
                    buttonText={t_signUp}
                />
                {userTests.length === 0 && creatingMode === CreationModes.TEST && (
                    <InformationBanner
                        iconName={"cards-2"}
                        isAnonymous={!!userInfo.isAnonymousUser}
                        anonymousText={t_ownContentAnonymous}
                        registeredText={t_useOwnContentInfo}
                        showButton={!userInfo.isAnonymousUser}
                        onButtonPress={handleContinueOwnSubjectsClick}
                        buttonText={t_continue}
                    />
                )}
            </AdditionalInfoWrapper>
            <NoOwnContentModal
                isOpen={isNoOwnContentModalOpen}
                closeModalCallback={() => setIsNoOwnContentModalOpen(false)}
                continueCallback={handleGoToWebAppClick}
            />
        </>
    );
};

export default BookSelectionBanners;
